import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import { useEntityReferenceOptions } from "@/hooks/use-entity-reference-options.hook.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useEmployeesByIds } from "@/queries/employees/use-employees.query.ts";
import { ApiField, ApiFieldValueTypes } from "@/types/custom-field.types.ts";
import { useOfferEditContext } from "@/types/recruit/offer.types.ts";
import { Button, ListItem } from "@skailer-com/uikit";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export function OfferPreviewCustomField({ field }: { field: ApiField }) {
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();
  const { getFieldTitle, getCustomFieldPreviewValue } = useCustomFieldsHook();
  const { formHook, offerRequest, offerConfig, handleUpdateConfig } = useOfferEditContext();
  const formData = formHook.getValues();
  const value = formData[field.slug];

  const referenceFields = useMemo(() => [field], [field]);
  const { optionsByEntity } = useEntityReferenceOptions(referenceFields, formData);
  const employeeIds =
    field.field_type === ApiFieldValueTypes.employee_reference ? (value as number[]) : [];
  const { data: employees } = useEmployeesByIds(employeeIds);

  const isVisibleToCandidate = !!field.config?.is_visible_to_candidate;
  const hiddenIds = offerConfig.hidden_custom_field_ids;
  const isHidden = !isVisibleToCandidate || hiddenIds.includes(field.id);

  const getValueTitle = () => {
    switch (field.field_type) {
      case ApiFieldValueTypes.entity_reference:
        return getCustomFieldPreviewValue(
          field,
          formData,
          field.reference_entity ? (optionsByEntity[field.reference_entity] ?? []) : []
        );
      case ApiFieldValueTypes.employee_reference:
        return employeeIds
          .map((id) => employees?.find((employee) => employee.id === id))
          .filter((employee) => employee !== undefined)
          .map((employee) => renderEmployeeName(employee))
          .join(", ");
      case ApiFieldValueTypes.file:
      case ApiFieldValueTypes.image: {
        const savedValue = offerRequest?.offer_data.custom_fields?.find(
          (item) => item.field.id === field.id
        )?.value;
        return savedValue?.value_file && savedValue.value_text === value
          ? savedValue.value_file.name
          : getCustomFieldPreviewValue(field, formData);
      }
      default:
        return getCustomFieldPreviewValue(field, formData);
    }
  };

  const toggleHidden = () =>
    handleUpdateConfig(
      "hidden_custom_field_ids",
      hiddenIds.includes(field.id)
        ? hiddenIds.filter((hiddenId) => hiddenId !== field.id)
        : [...hiddenIds, field.id]
    );

  return (
    <ListItem
      preTitle={getFieldTitle(field)}
      titleSlot={
        <div className={isHidden ? "line-through text-dark/60" : "text-title"}>
          {getValueTitle()}
        </div>
      }
      valueSlot={
        isVisibleToCandidate ? (
          <Button variant="plain" onClick={toggleHidden}>
            {isHidden ? t("common:show") : t("common:hide")}
          </Button>
        ) : undefined
      }
    />
  );
}
